/*
 * Declarative shadow DOM is only initialized on the initial render of the page.
 * If the component is mounted after the browser finishes the initial render,
 * the shadow root needs to be manually hydrated.
 */
/**
 * A custom ResizeObserver that only calls the callback when the element is resized.
 * By default the ResizeObserver callback is called when the element is first observed.
 */
class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  /**
   * @param {ResizeObserverCallback} callback
   */
  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

/**
 * Event class for overflow minimum items updates
 * @extends {Event}
 */
class OverflowMinimumEvent extends Event {
  /**
   * Creates a new OverflowMinimumEvent
   * @param {boolean} minimumReached - Whether the minimum number of visible items has been reached
   */
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = {
      minimumReached,
    };
  }
}

class ReflowEvent extends Event {
  /**
   * @param {HTMLElement} lastVisibleElement - The element to move to the last visible position
   */
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

/**
 * A custom element that wraps a list of items and moves them to an overflow slot when they don't fit.
 * This component is used in the header section, it needs to be render-blocking to avoid layout shifts.
 * @attr {string | null} minimum-items When set, the element enters a 'minimum-reached' state when visible items are at or below this number.
 * @example
 * <overflow-list minimum-items="2">
 *   <!-- list items -->
 * </overflow-list>
 */
class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else {
        this.#reflowItems();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const { shadowRoot } = this;

    if (!shadowRoot) throw new Error('Missing shadow root');

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      throw new Error('Invalid element types in <OverflowList />');
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    // Add event listener for reflow requests
    this.addEventListener(
      'reflow',
      /** @param {ReflowEvent} event */ (event) => {
        this.#reflowItems(event.detail.lastVisibleElement);
      }
    );

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        // Remove attribute first to change layout before calculating the actual size
        this.removeAttribute('defer');
        this.#reflowItems();
      };

      const idleCallback =
        typeof requestIdleCallback === 'function' ? requestIdleCallback : setTimeout;

      idleCallback(() => this.schedule(deferredReflow));
    } else {
      this.#reflowItems();
    }
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
  }

  get schedule() {
    return typeof Theme?.utilities?.scheduler?.schedule === 'function'
      ? Theme.utilities.scheduler.schedule
      : requestAnimationFrame;
  }

  #scheduled = false;

  /**
   * Get the minimum number of items before changing the minimum-reached state
   * @returns {number | null}
   */
  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    const { overflowSlot } = this.#refs;
    return overflowSlot;
  }

  get defaultSlot() {
    const { defaultSlot } = this.#refs;
    return defaultSlot;
  }

  /**
   * @type {{width: number | null, height: number | null}}
   */
  #lastDimensions = {
    width: null,
    height: null,
  };

  /**
   * @type {ResizeObserverCallback & MutationCallback}
   */
  #handleChange = (event) => {
    if (this.#scheduled) return;

    let width = null;
    let height = null;
    let isResize = false;

    for (const [, entry] of event.entries()) {
      if (!(entry instanceof ResizeObserverEntry)) break;
      // There should only be one entry
      isResize = true;
      width = Math.round(entry.contentRect.width);
      height = Math.round(entry.contentRect.height);
    }

    if (isResize) {
      if (
        !width ||
        !height ||
        (width === this.#lastDimensions.width && height === this.#lastDimensions.height)
      ) {
        // Skip reflow if dimensions are 0 or the same as the last reflow
        return;
      }

      this.#lastDimensions = { width: Math.round(width), height: Math.round(height) };
    }

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems();
      this.#scheduled = false;
    });
  };

  /**
   * Move all items to the default slot.
   */
  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  /**
   * Reset the list to its initial state and disconnect the observers.
   */
  #reset() {
    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    this.style.setProperty('--overflow-count', '0');
  }

  /**
   * Sets the minimum-reached attribute and dispatches a custom event based on visible elements count
   * @param {Element[]} visibleElements - The currently visible elements
   */
  #updateMinimumReached(visibleElements) {
    if (this.minimumItems !== null) {
      const minimumReached = visibleElements.length < this.minimumItems;

      if (minimumReached) {
        this.setAttribute('minimum-reached', '');
      } else {
        this.removeAttribute('minimum-reached');
      }

      this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
    }
  }

  /**
   * Show all items in the list.
   */
  showAll() {
    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  /**
   * Reflow items based on available space within the list.
   * @param {HTMLElement} [lastVisibleElement] Optional element to place in last visible position
   */
  #reflowItems = (lastVisibleElement) => {
    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    // Avoid layout shifts while reflowing the list
    const { height } = this.firstElementChild?.getBoundingClientRect() ?? {};

    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    // Move all elements to the default slot so we can check which ones overflow
    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();

    // Make sure the "More" item and placeholder are hidden
    moreSlot.hidden = true;
    placeholder.hidden = true;

    // First, check if all the items fit
    const rootRect = list.getBoundingClientRect();

    // Store the initial dimensions for comparison later
    this.#lastDimensions = {
      width: Math.round(rootRect.width),
      height: Math.round(rootRect.height),
    };

    const getVisibleElements = () =>
      elements.filter((el) => el.getBoundingClientRect().top <= rootRect.top);
    let visibleElements = getVisibleElements();

    // If not all items fit or we have a lastVisibleElement, let's calculate with "More" button
    if (visibleElements.length !== elements.length || lastVisibleElement) {
      // Putting the "More" item (and lastVisibleElement, if provided) at the start of the list lets us see which items will fit on the same row
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;
      moreSlot.style.setProperty('height', `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      // Recalculate the visible elements
      visibleElements = getVisibleElements();

      // Reset the order
      moreSlot.style.removeProperty('order');
      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');
      }

      // If we have a lastVisibleElement, ensure it's in the last visible position
      if (lastVisibleElement) {
        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          // Remove lastVisibleElement from its current position
          visibleElements.splice(visibleIndex, 1);
          // Add it to the end of visible elements
          visibleElements.push(lastVisibleElement);
        }
      }

      moreSlot.style.setProperty('height', 'auto');
    }

    const overflowingElements = elements.filter((element) => !visibleElements.includes(element));
    const [firstOverflowingElement] = overflowingElements;
    const hasOverflow = overflowingElements.length > 0;
    const placeholderWidth = firstOverflowingElement ? firstOverflowingElement.clientWidth : 0;

    // Move the elements to the correct slot
    for (const element of elements) {
      element.slot = overflowingElements.includes(element) ? overflowSlot.name : defaultSlot.name;
    }

    // If there are overflowing elements
    // Show more button and placeholder if needed
    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      // Set the width and height of the placeholder so the list can grow if there is space
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    // Check if the minimum-reached state should be updated
    hasOverflow && this.#updateMinimumReached(visibleElements);

    // Observe the list for changes in size
    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  };

  /**
   * @type {{
   *   defaultSlot: HTMLSlotElement;
   *   overflowSlot: HTMLSlotElement;
   *   moreSlot: HTMLSlotElement;
   *   overflow: HTMLElement;
   *   list: HTMLUListElement;
   *   placeholder: HTMLLIElement;
   * }}
   */
  #refs;

  /**
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeNotifier(this.#handleChange);

  /**
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver(this.#handleChange);
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

// Function to calculate total height of header group children
// Optimized to batch DOM reads before writes to prevent layout thrashing
function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group'),
  mainMenu = document.querySelector('#MainMenu'),
  mobileSearch = document.querySelector('#header-mobile-search'),
  navigationBar = document.querySelector('#navigationBar')
) {
  // Phase 1: Batch all DOM reads together to minimize layout recalculations
  let headerHeight = 0;
  let announcementHeight = 0;
  let mobileStickyHeaderHeight = 0;
  let headerGroupHeight = 0;
  let headerMenuHeight = 0;
  let headerMenuDefaultHeight = 0;
  let mobileSearchHeight = 0;
  let mobileSearchDefaultHeight = 0;
  let navigationBarHeight = 0;

  // Read header parent height
  if (header instanceof HTMLElement) {
    const parent = header.parentElement;
    if (parent) {
      headerHeight = parent.offsetHeight;
    }
  }

  // Read announcement bar height
  const announcementBar = document.querySelector('.announcement-bar');
  if (announcementBar instanceof HTMLElement) {
    announcementHeight = announcementBar.offsetHeight;
  }

  // Read mobile sticky header height
  if (header instanceof HTMLElement) {
    const headerDrawerContainer = header.querySelector('[ref="headerDrawerContainer"]');
    if (headerDrawerContainer instanceof HTMLElement) {
      mobileStickyHeaderHeight = headerDrawerContainer.offsetHeight;
    }
  }

  // Read header group children heights
  if (headerGroup instanceof HTMLElement) {
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element === header || !(element instanceof HTMLElement)) continue;
      headerGroupHeight += element.offsetHeight;
    }
  }

  // Read header sticky state once (used by multiple calculations)
  const isHeaderSticky = header && header.getAttribute('data-sticky-state') === 'active';

  // Read main menu height
  if (mainMenu instanceof HTMLElement) {
    headerMenuHeight = mainMenu.offsetHeight;
    headerMenuDefaultHeight = isHeaderSticky ? 45 : headerMenuHeight;
  }

  // Read mobile search height
  if (mobileSearch instanceof HTMLElement) {
    const mobileSearchDefaultHeightValue = 56;
    const scrollTop =
      document.scrollingElement?.scrollTop || document.documentElement.scrollTop || 0;

    // Read offsetHeight only once and reuse the value
    const mobileSearchOffsetHeight = mobileSearch.offsetHeight;
    mobileSearchHeight = scrollTop > 0 ? mobileSearchDefaultHeightValue : mobileSearchOffsetHeight;
    mobileSearchDefaultHeight = isHeaderSticky
      ? mobileSearchDefaultHeightValue
      : mobileSearchOffsetHeight;
  }

  // Read navigation bar height
  if (navigationBar instanceof HTMLElement) {
    navigationBarHeight = navigationBar.offsetHeight;
  }

  // Phase 2: Batch all DOM writes together after all reads are complete
  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--announcement-bar-height', `${announcementHeight}px`);
  document.body.style.setProperty('--mobile-sticky-header-height', `${mobileStickyHeaderHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
  document.body.style.setProperty('--header-menu-height', `${headerMenuHeight}px`);
  document.body.style.setProperty('--header-menu-default-height', `${headerMenuDefaultHeight}px`);
  document.body.style.setProperty('--header-mobile-search-height', `${mobileSearchHeight}px`);
  document.body.style.setProperty(
    '--header-mobile-search-default-height',
    `${mobileSearchDefaultHeight}px`
  );
  document.body.style.setProperty('--navigation-bar-height', `${navigationBarHeight}px`);
}

(() => {
  const previewBarData = document.getElementById('OnlineStorePreviewBarNextData');

  if (!(previewBarData instanceof HTMLElement)) return;

  document.body.classList.add('has-preview-bar');
})();
(() => {
  const methodsToWatch = ['pushState', 'replaceState'];

  // Wrap history methods in a Proxy so we can fire an event after they run
  methodsToWatch.forEach((method) => {
    // @ts-ignore
    const original = window.history[method];
    // @ts-ignore
    window.history[method] = new Proxy(original, {
      apply(target, thisArg, args) {
        // Perform the actual state change
        const returnValue = Reflect.apply(target, thisArg, args);
        // Notify everyone who cares
        window.dispatchEvent(new Event('locationchange'));
        return returnValue;
      },
    });
  });

  // Back/forward buttons still only trigger popstate
  window.addEventListener('popstate', () => {
    window.dispatchEvent(new Event('locationchange'));
  });
})();

(() => {
  if (window.__swCartFetchInterceptorInstalled) return;
  window.__swCartFetchInterceptorInstalled = true;

  const OFFER_CODE_STORAGE_KEY = 'sw_offer_code';
  const OFFER_CODE_CHANGE_EVENT = 'sw:offer-code-change';
  const originalFetch = window.fetch.bind(window);

  const normalizeOfferCode = (code) => {
    if (typeof code !== 'string') return null;
    const normalized = code.trim().toUpperCase();
    return normalized || null;
  };

  const emitOfferCodeChange = (offerCode) => {
    document.dispatchEvent(
      new CustomEvent(OFFER_CODE_CHANGE_EVENT, {
        detail: { offerCode },
      })
    );
  };

  const cacheOfferCode = (offerCode) => {
    try {
      const normalizedOfferCode = normalizeOfferCode(offerCode);
      const previousOfferCode = normalizeOfferCode(
        window.localStorage.getItem(OFFER_CODE_STORAGE_KEY)
      );

      if (normalizedOfferCode === previousOfferCode) return;

      if (normalizedOfferCode) {
        window.localStorage.setItem(OFFER_CODE_STORAGE_KEY, normalizedOfferCode);
      } else {
        window.localStorage.removeItem(OFFER_CODE_STORAGE_KEY);
      }

      emitOfferCodeChange(normalizedOfferCode);
    } catch (error) {
      console.error('Error caching offer code from cart update', error);
    }
  };

  const getRequestUrl = (input) => {
    if (typeof input === 'string') return input;
    if (input instanceof URL) return input.toString();
    if (input instanceof Request) return input.url;
    return '';
  };

  const isCartUpdateRequest = (input) => {
    try {
      const requestPath = new URL(getRequestUrl(input), window.location.origin).pathname;
      // Theme.routes.cart_update_url resolves to the form endpoint (/cart/update);
      // theme code POSTs to the AJAX endpoint (/cart/update.js) for JSON responses.
      // Match both so this interceptor catches every cart-update flow.
      const basePath = new URL(Theme.routes.cart_update_url, window.location.origin).pathname;
      return requestPath === basePath || requestPath === basePath + '.js';
    } catch {
      return false;
    }
  };

  const getResponseOfferCode = (payload) => {
    const discountCode = normalizeOfferCode(payload?.discount_codes?.[0]?.code);
    if (discountCode) return discountCode;

    return 'INT999';
  };

  const syncOfferCodeFromCartUpdate = async (response) => {
    if (!response.ok) return;

    try {
      const payload = await response.json();
      const responseOfferCode = getResponseOfferCode(payload);

      if (responseOfferCode) {
        cacheOfferCode(responseOfferCode);
        return;
      }

      if (Array.isArray(payload?.discount_codes) && payload.discount_codes.length === 0) {
        cacheOfferCode(null);
      }
    } catch (error) {
      console.error('Error reading cart update response for offer code caching', error);
    }
  };

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);

    if (isCartUpdateRequest(input)) {
      void syncOfferCodeFromCartUpdate(response.clone());
    }

    return response;
  };
})();

// Specs and Compare floating bar styles
const floatingCompareStyles = `
.floating-compare-bar {
  background-color: var(--floating-compare-bar-background);
  border-top: 1px solid var(--color-stone-600);
  box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.25);
}
.floating-compare-bar__product-list {
  grid-auto-columns: minmax(150px, 300px);
  gap: 16px;
  padding-inline: 0;
  margin-inline: 0;
}
.floating-compare-bar__product {
  padding: 12px 8px;
  display: flex;
  align-items: center;
  background: none;
  border: 1px solid var(--color-stone-600);
  border-radius: 0;
  container-type: inline-size;
}
.floating-compare-bar__product img {
  margin-inline: 0 8px;
  flex: none;
}
sc-floating-compare-bar .floating-compare-bar__product-title {
  color: currentColor;
  padding: 0;
  margin: 0;
  white-space: normal;
  text-align: left;
  line-height: 1.15;
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
}
.product__remove-button {
  position: static;
  order: 99;
  margin-left: auto;
  width: 40px;
  text-align: center;
  flex: none;
  transition: transform var(--animation-speed) var(--animation-easing);
}
.product__remove-button:hover {
  transform: scale(1.1);
}
.product__remove-button svg {
  display: none;
}
.product__remove-button:after {
  content: '✕';
  font-size: 20px;
  font-weight: bold;
  line-height: 20px;
  color: currentColor;
}
.floating-compare-bar__action-list .button {
  border-radius: 0 !important;
  padding: 7px 24px;
  font-size; 14px;
  line-height: 20px;
}
sc-floating-compare-bar sc-compare-dialog-open-button button.button:hover {
  background-color: rgba(0 132 69 / 1.0);
  color: #ffffff !important;
}
sc-floating-compare-bar sc-compare-clear-button button {
  color: currentColor !important;
}
@container (max-width: 175px) {
  .floating-compare-bar__product img {
    --floating-compare-bar-product-image-width: 40px;
    --floating-compare-bar-product-image-height: 40px;
    margin-inline: -4px 4px;
  }

  .product__remove-button {
    width: 32px;
    padding: 6px;
    margin-inline-end: -4px;
  }
}
`;
/** @param {string} cssText */
const sheet = (cssText) => {
  try {
    const s = new CSSStyleSheet();
    s.replaceSync(cssText);
    return s;
  } catch {
    return null;
  }
};
/**
 * @param {ShadowRoot|null} root
 * @param {string} cssText
 */
function styleShadowRoot(root, cssText) {
  if (!root) return;
  try {
    const styles = sheet(cssText);
    if (styles && 'adoptedStyleSheets' in root) {
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, styles];
    } else {
      const style = document.createElement('style');
      style.textContent = floatingCompareStyles;
      root.append(style);
    }
  } catch {
    // no-op—some engines or CSPs may block this
  }
}
/**
 * @param {Element} el
 * @param {string} cssText
 */
function styleOpenShadow(el, cssText) {
  const root = el.shadowRoot;
  if (!root) return;
  styleShadowRoot(root, cssText);
  el.classList.add('styled');
}

document.addEventListener('DOMContentLoaded', () => {
  document
    .querySelectorAll('sc-dynamic-compare')
    .forEach((el) => styleOpenShadow(el, floatingCompareStyles));
});

const origAttachShadow = Element.prototype.attachShadow;
/** @param {ShadowRootInit} init */
Element.prototype.attachShadow = function (init) {
  const root = origAttachShadow.call(this, init);
  if (this.tagName === 'SC-DYNAMIC-COMPARE') {
    styleShadowRoot(root, floatingCompareStyles);
  }
  return root;
};
